import { connect } from 'pwa-helpers';
import { TemplateResult, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import store, { waitForUser } from '../../store/store';
import '../product-card/product-card-display';
import { StoreState } from '../../store/store.interface';
import { BaseComponent } from '../base-component/base-component';
import { GetUserProfileState, UserProfile } from '../user-profile/user-profile.interface';
import { fetchUserProfile } from '../user-profile/user-profile.thunks';
import '../order-history/order-history';
import { ActionStatus } from '../../common/constants/action-status';
import environment from '../../common/environment';
import { booleanConverter } from '../../common/converters';
import '../inclusive-access-setting/inclusive-access-setting';
import emit from '../../common/events';
import { loading, PassValidationCompStatus } from '../../common/constants/api-constants';
import './security-tab/security-tab';
import './my-information-tab/my-information-tab';
import './my-account-access-link';
import './my-digital-products/my-digital-products';
import './sample-history/sample-history';
import './my-digital-samples/my-digital-samples';
import './my-digital-products-v2/my-digital-products-v2';
import { updatePassValidationCompStatus } from '../user-profile';
import { fetchUserProfileProducts } from '../user-profile/thunks/fetch-user-products.thunk';
import { getAnalyticsSafeData, isSegUser, selectUser } from '../user-profile/user-profile.selectors';
import { getAppFlow, getUserActiveTab } from '../user-profile/utils/user-profile.util';
import { updateUserSelf } from '../user-profile/thunks/fetch-user-self.thunk';
import { AccountPageAppFlow } from './app-flow.enum';
import "../wish-list/view/wish-list-my-account";
import { isLMSFlow } from '../../common/utils/my-account-utils';
import { ariaLabelWithNewTab } from '../../common/utils/a11y-util';
import { isHEInstructor } from '../../common/utils/cart-util';
import './my-learning-technology-rep/my-learning-technology-rep';

/**
 * This object represents one of the options of the my account tab selector
 */
declare interface MyAccountTab {
  title: string;
  hash: string;
  id: string;
  content: TemplateResult;
}

const SAFE_URL_PATTERN = /^(?:(?:https?|mailto|ftp|tel|file):|[^&:/?#]*(?:[/?#]|$))/gi;
const DATA_URL_PATTERN = /^data:(?:image\/(?:bmp|gif|jpeg|jpg|png|tiff|webp)|video\/(?:mpeg|mp4|ogg|webm)|audio\/(?:mp3|oga|ogg|opus));base64,[a-z0-9+\/]+=*$/i;

/**
 * The my account tabs
 */
const tabsData: Array<MyAccountTab> = [
  {
    title: 'My Information',
    hash: 'myInfo',
    id: '_myInfo',
    content: html`<mhecomm-my-information-tab></mhecomm-my-information-tab>`,
  },
  {
    title: 'Security & Login',
    hash: 'securityLogin',
    id: '_securityLogin',
    content: html`<mhecomm-security-tab></mhecomm-security-tab>`,
  },
  {
    title: 'Order History',
    hash: 'orderHistory',
    id: '_orderHistory',
    content: html`<mhecomm-order-history></mhecomm-order-history>`,
  },
  {
    title: 'Sample History',
    hash: 'sampleHistory',
    id: '_sampleHistory',
    content: html`<mhecomm-sample-history></mhecomm-sample-history>`,
  },
  {
    title: 'My Digital Products',
    hash: 'myDigitalProducts',
    id: '_myDigitalProducts',
    content: html`<mhecomm-my-digital-products></mhecomm-my-digital-products>`
  },
  {
    title: 'My Digital Samples',
    hash: 'myDigitalSamples',
    id: '_myDigitalSamples',
    content: html`<mhecomm-my-digital-samples></mhecomm-my-digital-samples>`
  },
  {
    title: 'My Learning Technology Rep',
    hash: 'myLTR',
    id: '_myLTR',
    content: html`<mhecomm-my-learning-technology-rep></mhecomm-my-learning-technology-rep>`
  },
  {
    title: 'My Lists',
    hash: 'myLists',
    id: '_myLists',
    content: html`<mhecomm-my-account-wish-list></mhecomm-my-account-wish-list>`
  }
].filter(tab => {
  if(isLMSFlow() && tab.id === "_securityLogin") {
    return false;
  } else {
    return true ;
  }
});

@customElement('mhecomm-my-account')
export class MyAccount extends connect(store)(BaseComponent) {

  @property({ converter: booleanConverter })
  enableV2 = false;

  @state()
  private loading = true;

  @state()
  private activeTabId = tabsData[0].id;

  private user: UserProfile | undefined;
  private deleteAccountSupportLinkId = 'my-account-delete-account-support-link';
  private deleteAccountSupportLinkText = 'support';
  private myAccountBackButtonId = 'my-account-back-button';
  private myAccountBackButtonLabel = 'Back';

  constructor() {
    super();
    this.id = "my-account"
    this.onHashChange = this.onHashChange.bind(this);

    // Set activeTabId from URL hash so the correct tab renders on first paint
    if (window.location.hash) {
      const hashTabId = '_' + window.location.hash.substring(1);
      const matchingTab = tabsData.find(tab => tab.id === hashTabId);
      if (matchingTab) {
        this.activeTabId = hashTabId;
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    if (getAppFlow() === AccountPageAppFlow.Create) {
      store.dispatch(updateUserSelf());
    }
    store.dispatch(fetchUserProfile());
    waitForUser().then(() => {
      emit({
        element: this,
        name: 'page-load',
        detail: {
          comp: 'my-account-page',
          user: getAnalyticsSafeData(this.user!),
          appFlow: getAppFlow(),
          myAccountSection: this.getTitleById(this.activeTabId),
          id:this.id
        },
        dataLayer: true,
      });
    });
    window.addEventListener('hashchange', this.onHashChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('hashchange', this.onHashChange);
  }

  private onHashChange() {
    const hashTabId = '_' + window.location.hash.substring(1);
    const matchingTab = tabsData.find(tab => tab.id === hashTabId);
    if (matchingTab) {
      this.activeTabId = hashTabId;
    }
  }

  async stateChanged(state: StoreState) {
    this.user = selectUser(state);
    this._checkForRedirectToLogin(state.userProfileState.userProfileResponse);
  }

  getTitleById(id: string): string | undefined {
    const tab = tabsData.find(tab => tab.id === id);
    return tab ? tab.title : undefined;
  }

  _checkForRedirectToLogin(userProfileResponse: GetUserProfileState) {
    if (window.Mhecomm_Editor) {
      return;
    }

    if (userProfileResponse.status === ActionStatus.failed) {
      window.location.href = environment().loginUrl;
    }

    if (userProfileResponse.status === ActionStatus.succeeded) {
      this.loading = false;
      if (!userProfileResponse.data) {
        window.location.href = environment().loginUrl;
      }
    }
  }

  /**
   * Remove shadow DOM
   * @returns
   */
  createRenderRoot() {
    return this;
  }

  renderComp() {
    return html`
      <button
        id="${this.myAccountBackButtonId}"
        class="btn btn-link ps-0 mb-2"
        data-automation-id="my-account-back"
        @click="${() => this.redirect()}"
      >
        ${this.uxtIconReady ? html`<uxt-icon icon="chevron-left" class="pe-2"></uxt-icon>` : ''}
        ${this.myAccountBackButtonLabel}
      </button>
      <h1 id="account-heading" class="fw-medium">My Account</h1>
      ${this._renderTabs()}
      <p class="p-2 rounded-2 bg-light border">
        <strong>Account Deletion:</strong> If you need to delete your account or change your account type, please reach
        out to
        <a
          id="${this.deleteAccountSupportLinkId}"
          href="https://www.mheducation.com/support.html"
          @click=${this.onClickDeleteAccountSupportLink}
          aria-label="${ariaLabelWithNewTab(this.deleteAccountSupportLinkText)}"
          target="_blank"
          >${this.deleteAccountSupportLinkText}</a
        >.
      </p>
    `;
  }

  onClickDeleteAccountSupportLink(event: Event) {
    event.preventDefault();
    const link = event.currentTarget as HTMLAnchorElement;
    emit({
      element: this,
      name: 'click',
      detail: {
        id: this.deleteAccountSupportLinkId,
        label: this.deleteAccountSupportLinkText,
        comp: 'link',
        user: getAnalyticsSafeData(this.user!),
        appFlow: getAppFlow(),
        href: link.href,
        myAccountSection: getUserActiveTab(),
      },
      dataLayer: true,
    });
    window.open(link.href, '_blank');
  }

  async redirect() {
    const queryParams = new URL(location.href).searchParams;
    const redirectUrlParam = queryParams.get('redirectUrl');
    const productId = queryParams.get('productId');
    let url;

    if (redirectUrlParam) {
      url = redirectUrlParam.trim();
    } else if (productId) {
      const product = await store.dispatch(fetchUserProfileProducts(productId)).unwrap();
      url = product.productHomePage;
    }

    const analicObj: any = {
      element: this,
      name: 'click',
      detail: {
        id: this.myAccountBackButtonId,
        label: this.myAccountBackButtonLabel,
        comp: 'button',
        user: getAnalyticsSafeData(this.user!),
        appFlow: getAppFlow(),
      },
      dataLayer: true,
    };

    const isURLValid = this.isURLValid(url);
    const corpURL = environment().mheducationUrl;

    if (url && isURLValid) {
      analicObj.detail.href = url;
    } else {
      analicObj.detail.href = url ? corpURL : null;
    }
    
    emit(analicObj);
    
    if (url && isURLValid) {
      window.location.href = url;
    } else if (url) {
      window.location.href = corpURL;
    } else {
      window.history.back();
    }
  }

  isURLValid(url) {
    if (!url) return false;
    
    const ALLOWED_DOMAINS = ['mheducation.com', 'blackboard.com'];

    const matchesPattern = url.match(SAFE_URL_PATTERN) || url.match(DATA_URL_PATTERN);
    const usesHttps = url.startsWith('https://');
    const hostname = new URL(url).hostname;
    const domainAllowed = ALLOWED_DOMAINS.some(domain => hostname === domain || hostname.endsWith(`.${domain}`));

    return matchesPattern && usesHttps && domainAllowed;
  }

  _renderTabs() {
    if (this.loading) {
      return this._renderLoadingTemplate();
    }
    return this._tabs();
  }

  _renderLoadingTemplate() {
    return html`<div class="loading-container">
      ${loading({ height: '2.25rem', width: '100%', m: 'mt-4' })} ${loading({ height: '2.25rem', width: '100%' })}
      ${loading({ height: '2.25rem', width: '100%' })} ${loading({ height: '2.25rem', width: '100%' })}
      ${loading({ height: '2.25rem', width: '100%' })}
    </div>`;
  }

  _tabs() {
    return html`<div id="horizontal-tabs" class="bs5Tabs horizontal-options-tabs horizontal-tabs tab-style-primary">
      <div class="tabs-container">
        <button tabindex="-1" class="left-scroll-btn d-none">
          ${this.uxtIconReady ? html`<uxt-icon icon="chevron-left"></uxt-icon>` : ''}
        </button>

        <ul id="tabs" class="nav nav-tabs align-items-end pt-1" role="tablist" aria-orientation="horizontal" aria-labelledby="account-heading">
          ${tabsData
            .filter(tab => this._showMyList(tab))
            .map((tab) => this._tabButtons(tab))}
        </ul>

        <button tabindex="-1" class="right-scroll-btn d-none">
          ${this.uxtIconReady ? html`<uxt-icon icon="chevron-right"></uxt-icon>` : ''}
        </button>
      </div>
      <div class="tab-content border-0 tab-style-primary horizontal w-100" id="tabContent">
        ${tabsData
          .filter(tab => this._showMyList(tab))
          .map((tab) => this._tabContent(tab))}
      </div>
    </div> `;
  }

  _showMyList(tab) {
    const instructor = !!this.user && isHEInstructor(this.user);

    if (tab.id === '_myLTR') {
      return instructor;
    }

    if (tab.title === 'My Lists') {
      return isSegUser(this.user!);
    }

    if (instructor) {
      return tab.title !== 'Order History' && tab.title !== 'My Digital Products';
    }

    return tab.title !== 'Sample History' && tab.title !== 'My Digital Samples';
  }

  _tabButtons(tab) {
    return html`<li class="nav-item horizontal-tab" role="presentation">
      <a
        id="${tab.id}-tab"
        class="nav-link p-3 text-nowrap text-decoration-none text-muted fw-bold fs-6 ${tab.id === this.activeTabId
          ? ' active'
          : ''}"
        href="javascript:void(0)"
        tabindex="0"
        data-bs-target="#${tab.id}"
        type="button"
        role="tab"
        aria-controls="${tab.id}"
        aria-selected="${tab.id === this.activeTabId ? 'true' : 'false'}"
        @click=${(event) => this._onTabClick(tab, event)}
      >
        ${tab.title}
      </a>
    </li>`;
  }

  _tabContent(tab) {
    const content = tab.id === '_myDigitalProducts' && this.enableV2
      ? html`<mhecomm-my-digital-products-v2></mhecomm-my-digital-products-v2>`
      : tab.content;
    return html`<div
      class="tab-pane fade ${tab.id === this.activeTabId ? 'show active' : ''}"
      id="${tab.id}"
      role="tabpanel"
      aria-labelledby="${tab.id}-tab"
    >
      <div
        id="${tab.id}-content"
        class="${tab.id === this.activeTabId ? ' show' : ''}"
        data-bs-parent="#tabContent"
      >
        <div class="p-3" tabindex="-1">
          ${tab.id === this.activeTabId ? content : nothing}
        </div>
      </div>
    </div>`;
  }

  async _onTabClick(tab, _e) {
    const viewerType = !!this.user && isHEInstructor(this.user) ? 'Instructor' : 'Student';

    window.location.hash = `#${tab.id.substr(1)}`;
    this.activeTabId = tab.id;
    emit({
      element: this,
      name: 'click',
      detail: {
        eventName: tab.id,
        label: tab.title,
        comp: `tab`,
        user: getAnalyticsSafeData(this.user!),
        appFlow: getAppFlow(),
        viewerType,
      },
      dataLayer: true,
    });
    await this.updateComplete;
    store.dispatch(updatePassValidationCompStatus(PassValidationCompStatus.default));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-my-account': MyAccount;
  }
}